<template>
  <div class="wizard">
    <div class="wizard-content">
      <transition name="fade" mode="out-in">
        <div :key="activeStepId">
          <slot :name="activeStepId" />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import clsx from 'clsx';

export default {
  props: {
    steps: {
      type: Array,
      default: () => [],
    },
    machine: {
      type: Object,
      default: null,
    },
    context: {
      type: Object,
      default: () => ({}),
    },
    initialStep: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      currentIndex: 0,
      machineStep: null,
    };
  },
  created() {
    if (this.isMachineMode && this.steps.length > 0) {
      const initial = this.initialStep && this.steps.some((s) => s.id === this.initialStep)
        ? this.initialStep
        : this.steps[0].id;
      this.machineStep = initial;
    } else if (this.steps.length > 0) {
      const initialIndex = this.initialStep
        ? this.steps.findIndex((s) => s.id === this.initialStep)
        : -1;
      this.currentIndex = initialIndex >= 0 ? initialIndex : 0;
    }
  },
  computed: {
    isMachineMode() {
      return !!this.machine;
    },
    activeStepId() {
      if (this.isMachineMode) return this.machineStep;
      return this.steps[this.currentIndex]?.id ?? null;
    },
    currentStep() {
      if (this.isMachineMode) {
        return this.steps.find((s) => s.id === this.machineStep) ?? null;
      }
      return this.steps[this.currentIndex] ?? null;
    },
    isFirst() {
      if (this.isMachineMode) {
        return !this.machine.canGoPrevious(this.machineStep, this.context);
      }
      return this.currentIndex === 0;
    },
    isLast() {
      if (this.isMachineMode) {
        return this.machine.isTerminal(this.machineStep);
      }
      return this.currentIndex === this.steps.length - 1;
    },
    canGoNext() {
      if (this.isMachineMode) {
        return this.machine.canGoNext(this.machineStep, this.context);
      }
      return !this.isLast;
    },
    canGoPrev() {
      return !this.isFirst;
    },
  },
  methods: {
    clsx,
    next() {
      if (!this.canGoNext) return;

      if (this.isMachineMode) {
        const nextStep = this.machine.resolveNext(this.machineStep, this.context);
        if (nextStep) {
          this.machineStep = nextStep;
          this.$emit('step-change', this.machineStep, this.currentStep);
        }
        return;
      }

      this.currentIndex += 1;
      this.$emit('step-change', this.currentIndex, this.currentStep);
    },
    prev() {
      if (!this.canGoPrev) return;

      if (this.isMachineMode) {
        const prevStep = this.machine.resolvePrevious(this.machineStep, this.context);
        if (prevStep) {
          this.machineStep = prevStep;
          this.$emit('step-change', this.machineStep, this.currentStep);
        }
        return;
      }

      this.currentIndex -= 1;
      this.$emit('step-change', this.currentIndex, this.currentStep);
    },
    goToStep(indexOrId) {
      if (this.isMachineMode) {
        const id = typeof indexOrId === 'number'
          ? this.steps[indexOrId]?.id
          : indexOrId;

        if (id && this.machine.canTransition(this.machineStep, id)) {
          this.machineStep = id;
          this.$emit('step-change', this.machineStep, this.currentStep);
        }
        return;
      }

      const index = indexOrId;
      if (index < this.currentIndex) {
        this.currentIndex = index;
        this.$emit('step-change', this.currentIndex, this.currentStep);
      }
    },
    finish() {
      this.$emit('finish');
    },
    getCurrentStep() {
      return this.currentStep;
    },
    getCurrentIndex() {
      if (this.isMachineMode) {
        return this.machine.getStepIndex(this.machineStep);
      }
      return this.currentIndex;
    },
  },
};
</script>

<style scoped lang="scss">
.wizard {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.wizard-steps {
  display: flex;
  align-items: center;
  justify-content: center;
}

.wizard-step {
  display: flex;
  align-items: center;
  gap: 8px;

  &__indicator {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid #D4D4D8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: #A1A1AA;
    flex-shrink: 0;
    transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: #A1A1AA;
    white-space: nowrap;
    transition: color 0.2s ease;
  }

  &__connector {
    width: 48px;
    height: 2px;
    background-color: #D4D4D8;
    margin: 0 8px;
    flex-shrink: 0;
    transition: background-color 0.2s ease;
  }

  &--active {
    .wizard-step__indicator {
      border-color: #FB923C;
      color: #FB923C;
    }

    .wizard-step__label {
      color: #09090B;
    }
  }

  &--completed {
    .wizard-step__indicator {
      border-color: #FB923C;
      background-color: #FB923C;
      color: #fff;
      cursor: pointer;
    }

    .wizard-step__label {
      color: #09090B;
    }

    .wizard-step__connector {
      background-color: #FB923C;
    }
  }
}

.wizard-content {
  min-height: 120px;
}

.wizard-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.wizard-btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }

  &--primary {
    background-color: #FB923C;
    color: #fff;
  }

  &--secondary {
    background-color: transparent;
    border: 2px solid #D4D4D8;
    color: #09090B;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
